import React, { useState } from "react";
import { Box, Button, Sheet, Stack, Typography } from "@mui/joy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { theme } from "styles/theme";
import CustomModal from "components/common/CustomModal";
import { AddIcon } from "static/image/common/IconAssets";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import type { TaskBlocksPatch } from "./agent/types";
import { useAgentChatThread } from "./agent/useAgentChatThread";
import { useStickToBottomScroll } from "./agent/useStickToBottomScroll";
import { PreviousThreadsDropdown } from "./agent/PreviousThreadsDropdown";
import {
  AgentLivingLogo,
  AssistantTurnView,
  ChatInput,
  PresetScenariosBar,
  PRESET_SCENARIOS,
  UserBubble,
} from "./agent/components";
import type { PresetScenario } from "./agent/components";

interface AgentChatPanelProps {
  taskId?: string;
  currentTask?: TaskEditorState;
  onTaskPatch: (patch: TaskBlocksPatch) => void;
  onTurnStart?: () => void;
  onTurnEnd?: () => void;
  onRevertTurn?: (taskBefore: TaskEditorState) => void;
}

export default function AgentChatPanel({
  taskId,
  currentTask,
  onTaskPatch,
  onTurnStart,
  onTurnEnd,
  onRevertTurn,
}: AgentChatPanelProps) {
  const {
    turns,
    isStreaming,
    send,
    respondToApproval,
    clear,
    threadId,
    revertTurn,
    switchThread,
  } = useAgentChatThread(taskId, onTaskPatch, { onTurnStart, onTurnEnd });

  const handleRevertTurn = (turnId: string) => {
    const before = revertTurn(turnId);
    if (before && onRevertTurn) onRevertTurn(before);
  };
  const [input, setInput] = useState("");
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const { scrollRef, isAtBottom, hasOverflow, onScroll, scrollToBottom } =
    useStickToBottomScroll([turns, isStreaming]);

  const isDisabled = !taskId;

  const handleSend = () => {
    if (isDisabled || isStreaming) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    void send(trimmed, currentTask);
    setInput("");
  };

  const handlePresetPick = (scenario: PresetScenario) => {
    if (isDisabled || isStreaming) return;
    void send(scenario.message, currentTask);
    setInput("");
  };

  const showEmptyState = !isDisabled && turns.length === 0;

  return (
    <Sheet
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        borderRadius: 0,
        backgroundColor: theme.vars.palette.Background.Level1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "12px",
          py: "8px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          background: `linear-gradient(180deg, ${theme.vars.palette.Surface.Forward} 0%, ${theme.vars.palette.Background.Level1} 100%)`,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            minWidth: 28,
            minHeight: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AgentLivingLogo size={15} active />
        </Box>
        <Typography
          level="title-sm"
          sx={{
            flex: 1,
            fontSize: "13px",
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
          }}
        >
          Agent
        </Typography>
        {taskId && (
          <PreviousThreadsDropdown
            taskId={taskId}
            currentThreadId={threadId}
            onSelectThread={(tid) => void switchThread(tid)}
          />
        )}
        <Button
          size="sm"
          variant="Plain"
          startDecorator={<AddIcon size={12} />}
          onClick={() => setConfirmClearOpen(true)}
          disabled={isDisabled || turns.length === 0}
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            minHeight: "28px",
          }}
        >
          새 대화
        </Button>
      </Box>

      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          // 스크롤바 자리를 좌/우 양쪽에 예약해 인풋 영역과 가운데 정렬을 일치시킴.
          scrollbarGutter: "stable both-edges",
          pt: "14px",
          pb: "24px",
          px: "12px",
          backgroundColor: theme.vars.palette.Background.Level1,
          position: "relative",
          // floating button 이 항상 컨테이너 bottom 에 sticky 하게 자리잡도록
          // flex column + 마지막 자식의 mt: auto 패턴 사용.
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isDisabled ? (
          <Typography
            level="body-sm"
            sx={{
              color: theme.vars.palette.Content.Weak,
              textAlign: "center",
              mt: "32px",
            }}
          >
            활동지를 선택하면 채팅이 활성화됩니다.
          </Typography>
        ) : showEmptyState ? (
          <EmptyState onPick={handlePresetPick} />
        ) : (
          <Stack
            spacing={1.5}
            sx={{ maxWidth: "760px", mx: "auto", width: "100%" }}
          >
            {turns.map((turn, idx) => {
              const isLast = idx === turns.length - 1;
              if (turn.role === "user") {
                return <UserBubble key={turn.id} turn={turn} />;
              }
              return (
                <AssistantTurnView
                  key={turn.id}
                  turn={turn}
                  isLast={isLast}
                  isStreaming={isStreaming}
                  currentTask={currentTask}
                  onDecide={(decisions) =>
                    respondToApproval(turn.id, decisions, currentTask)
                  }
                  onRevertTurn={() => handleRevertTurn(turn.id)}
                />
              );
            })}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          position: "relative",
          maxWidth: "760px",
          mx: "auto",
          width: "calc(100% - 24px)",
          mb: "12px",
          borderRadius: "16px",
          backgroundColor: theme.vars.palette.Surface.Forward,
          border: `1px solid ${theme.vars.palette.Border.Weak}`,
          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.08)",
        }}
      >
        {hasOverflow && !isAtBottom && !showEmptyState && !isDisabled && (
          <Box
            component="button"
            type="button"
            onClick={scrollToBottom}
            aria-label="맨 아래로 이동"
            sx={{
              position: "absolute",
              // 인풋 floating 카드 바로 위 — 카드와 가까운 간격 유지.
              top: "-34px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 26,
              height: 26,
              borderRadius: "999px",
              backgroundColor: theme.vars.palette.Surface.Forward,
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)",
              color: theme.vars.palette.Content.Strong,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              zIndex: 2,
              transition: "background-color 0.12s ease, color 0.12s ease",
              "&:hover": {
                backgroundColor: theme.vars.palette.Background.Level2,
                color: theme.vars.palette.Content.Highlight,
              },
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: "18px" }} />
          </Box>
        )}
        <PresetScenariosBar
          onPick={handlePresetPick}
          disabled={isDisabled || isStreaming}
        />

        <ChatInput
          value={input}
          onChange={setInput}
          onSubmit={handleSend}
          disabled={isDisabled || isStreaming}
          placeholder={
            isDisabled
              ? "활동지를 먼저 선택하세요"
              : isStreaming
                ? "응답 받는 중…"
                : "작업을 설명하세요 (Enter 전송, Shift+Enter 줄바꿈)"
          }
        />
      </Box>

      <CustomModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        title="새 대화"
        subtitle="지금까지의 대화를 마치고 새로 시작할까요? 이전 대화는 상단 '대화 내역' 에서 다시 열어볼 수 있어요."
        actions={[
          {
            label: "새 대화 시작하기",
            variant: "Solid",
            color: "primary",
            onClick: () => {
              clear();
              setConfirmClearOpen(false);
            },
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: () => setConfirmClearOpen(false),
          },
        ]}
      />
    </Sheet>
  );
}

function EmptyState({ onPick }: { onPick: (s: PresetScenario) => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        py: "48px",
        textAlign: "center",
      }}
    >
      <AgentLivingLogo size={64} />
      <Stack spacing={0.5} alignItems="center">
        <Typography
          level="title-sm"
          sx={{
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
            fontSize: "14px",
          }}
        >
          Agent 와 함께 활동지 제작
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            color: theme.vars.palette.Content.Weak,
            maxWidth: "260px",
            fontSize: "12px",
            lineHeight: 1.5,
          }}
        >
          아래에서 시나리오를 선택하거나 직접 메시지를 입력해보세요.
        </Typography>
      </Stack>
      <Stack spacing={0.75} sx={{ width: "100%", maxWidth: "300px" }}>
        {PRESET_SCENARIOS.slice(0, 4).map((scenario) => (
          <Box
            key={scenario.id}
            onClick={() => onPick(scenario)}
            sx={{
              px: "12px",
              py: "10px",
              borderRadius: "8px",
              backgroundColor: theme.vars.palette.Surface.Forward,
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: theme.vars.palette.primary.solid,
                backgroundColor: theme.vars.palette.Background.Level1,
                transform: "translateY(-1px)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Typography
              level="body-sm"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {scenario.label}
            </Typography>
            <Typography
              level="body-xs"
              sx={{
                fontSize: "11px",
                color: theme.vars.palette.Content.Weak,
                mt: "2px",
              }}
            >
              {scenario.description}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
